import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
    DragStart,
    DragUpdate,
    DropResult,
    ResponderProvided,
} from '@hello-pangea/dnd'

import type { PageEditorDragType, PageEditorRowType } from './PageEditor.types'

type UsePageEditorDragAndDropParams = {
    rows: PageEditorRowType[]
    setRows: Dispatch<SetStateAction<PageEditorRowType[]>>
}

const usePageEditorDragAndDrop = ({
    rows,
    setRows,
}: UsePageEditorDragAndDropParams) => {
    const { t } = useTranslation()
    const [isDragging, setIsDragging] = useState<PageEditorDragType>(false)

    const handleDragStart = (start: DragStart, provided: ResponderProvided) => {
        const { type } = start

        const objectType =
            type === 'ROW' ? t('page.this-row') : t('page.this-widget')

        provided.announce(t('page.use-arrow-keys-to-move', { objectType }))

        if (type === 'ROW' || type === 'WIDGET') {
            setIsDragging(type)
        }
    }

    const handleDragUpdate = (
        update: DragUpdate,
        provided: ResponderProvided,
    ) => {
        const { source, destination, type } = update
        const objectType =
            type === 'ROW' ? t('page.the-row') : t('page.the-widget')

        if (!destination) {
            return
        }

        const confirmText = t('page.confirm-with-spacebar-cancel-with-escape')

        if (type === 'WIDGET') {
            const sourceIndex = source.droppableId.split('-')
            const destinationIndex = destination.droppableId.split('-')
            const destinationRow = Number(destinationIndex[0]) + 1
            const destinationColumn = Number(destinationIndex[1]) + 1

            if (sourceIndex[0] !== destinationIndex[0]) {
                provided.announce(
                    `${t('page.item-moved-to-row', {
                        objectType,
                        destinationRow,
                    })} ${confirmText}`,
                )
                return
            }

            if (sourceIndex[1] !== destinationIndex[1]) {
                provided.announce(
                    `${t('page.item-moved-to-column', {
                        objectType,
                        destinationColumn,
                    })} ${confirmText}`,
                )
                return
            }

            if (source.index !== destination.index) {
                provided.announce(
                    `${t('page.item-moved-to-position', {
                        objectType,
                        destinationPosition: destination.index + 1,
                    })} ${confirmText}`,
                )
                return
            }
        }

        if (type === 'ROW' && source.index !== destination.index) {
            provided.announce(
                `${t('page.item-moved-to-position', {
                    objectType,
                    destinationPosition: destination.index + 1,
                })} ${confirmText}`,
            )
            return
        }

        provided.announce(
            `${t('page.item-not-moved', { objectType })} ${confirmText}`,
        )
    }

    const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
        const { type, source, destination, reason } = result
        if (type === 'ROW') {
            if (
                reason === 'CANCEL' ||
                !destination ||
                source?.index === destination?.index
            ) {
                provided.announce(
                    t('page.item-not-moved', {
                        objectType: t('page.the-row'),
                    }),
                )
            } else {
                provided.announce(
                    t('page.row-moved-from-to', {
                        sourcePosition: source.index + 1,
                        destinationPosition: destination.index + 1,
                    }),
                )
            }
        } else if (type === 'WIDGET') {
            if (reason === 'CANCEL' || !destination) {
                provided.announce(
                    t('page.item-not-moved', {
                        objectType: t('page.the-widget'),
                    }),
                )
            } else {
                const sourceIndex = source.droppableId.split('-')
                const destinationIndex = destination.droppableId.split('-')
                const sourceRow = Number(sourceIndex[0]) + 1
                const sourceColumn = Number(sourceIndex[1]) + 1
                const destinationRow = Number(destinationIndex[0]) + 1
                const destinationColumn = Number(destinationIndex[1]) + 1

                provided.announce(
                    t('page.widget-moved-from-to', {
                        sourceRow,
                        sourceColumn,
                        sourcePosition: source.index + 1,
                        destinationRow,
                        destinationColumn,
                        destinationPosition: destination.index + 1,
                    }),
                )
            }
        }

        setIsDragging(false)

        if (!destination) {
            return
        }

        const sourceIndex = source.index
        const destinationIndex = destination.index

        if (type === 'ROW') {
            const draggedRow = rows[sourceIndex]

            setRows((prev) => {
                const newState = [...prev]
                newState.splice(sourceIndex, 1)
                newState.splice(destinationIndex, 0, draggedRow)
                return newState
            })
        }

        if (type === 'WIDGET') {
            const sourceIdArray = source.droppableId.split('-')
            const sourceRowIndex = parseInt(sourceIdArray[0], 10)
            const sourceColumnIndex = parseInt(sourceIdArray[1], 10)

            const destinationIdArray = destination.droppableId.split('-')
            const destinationRowIndex = parseInt(destinationIdArray[0], 10)
            const destinationColumnIndex = parseInt(destinationIdArray[1], 10)

            const draggedWidget =
                rows[sourceRowIndex].columns[sourceColumnIndex].widgets[
                    sourceIndex
                ]

            setRows((prev) =>
                prev.map((row, rIdx) => {
                    if (
                        rIdx === sourceRowIndex &&
                        sourceRowIndex === destinationRowIndex
                    ) {
                        return {
                            ...row,
                            columns: row.columns.map((col, cIdx) => {
                                if (
                                    cIdx === sourceColumnIndex &&
                                    sourceColumnIndex === destinationColumnIndex
                                ) {
                                    const newWidgets = [...col.widgets]
                                    const [removed] = newWidgets.splice(
                                        sourceIndex,
                                        1,
                                    )
                                    newWidgets.splice(
                                        destinationIndex,
                                        0,
                                        removed,
                                    )
                                    return { ...col, widgets: newWidgets }
                                }
                                if (cIdx === sourceColumnIndex) {
                                    return {
                                        ...col,
                                        widgets: col.widgets.filter(
                                            (_, wIdx) => wIdx !== sourceIndex,
                                        ),
                                    }
                                }
                                if (cIdx === destinationColumnIndex) {
                                    const newWidgets = [...col.widgets]
                                    newWidgets.splice(
                                        destinationIndex,
                                        0,
                                        draggedWidget,
                                    )
                                    return { ...col, widgets: newWidgets }
                                }
                                return col
                            }),
                        }
                    }
                    if (rIdx === sourceRowIndex) {
                        return {
                            ...row,
                            columns: row.columns.map((col, cIdx) =>
                                cIdx === sourceColumnIndex
                                    ? {
                                          ...col,
                                          widgets: col.widgets.filter(
                                              (_, wIdx) => wIdx !== sourceIndex,
                                          ),
                                      }
                                    : col,
                            ),
                        }
                    }
                    if (rIdx === destinationRowIndex) {
                        return {
                            ...row,
                            columns: row.columns.map((col, cIdx) => {
                                if (cIdx === destinationColumnIndex) {
                                    const newWidgets = [...col.widgets]
                                    newWidgets.splice(
                                        destinationIndex,
                                        0,
                                        draggedWidget,
                                    )
                                    return { ...col, widgets: newWidgets }
                                }
                                return col
                            }),
                        }
                    }
                    return row
                }),
            )
        }
    }

    return {
        isDragging,
        handleDragStart,
        handleDragUpdate,
        handleDragEnd,
    }
}

export default usePageEditorDragAndDrop
